import { endPoints, PaymentEndpoints } from "../constant/Environment";
import { getAllData } from "./API";
import { getStoredSessionId } from "./session";

const CURRENCY_CODE_REGEX = /\b([A-Za-z]{3})\b/;

const toTwoDecimalString = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value;
  }

  return numericValue.toFixed(2);
};

const DEFAULT_PACKAGE_BASE_CURRENCY = "USD";

const shouldUseConvertedPackagesEndpoint = () => {
  const conversionFlag = (
    process.env.REACT_APP_ENABLE_PAYMENT_PACKAGE_CONVERSION || ""
  )
    .trim()
    .toLowerCase();

  // Fail-safe: keep conversion endpoint disabled unless explicitly enabled.
  if (conversionFlag !== "true") {
    return false;
  }

  if (typeof window === "undefined") {
    return true;
  }

  const host = window.location.hostname || "";
  const isIpv4Host = /^(\d{1,3}\.){3}\d{1,3}$/.test(host);

  // During direct IP deployments, payment conversion route is often unavailable.
  // Keep buy flow stable by using casino packages only.
  return !isIpv4Host;
};

const getCurrencyFromPersistedAuth = () => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const rootStateRaw = localStorage.getItem("persist:root");
    if (!rootStateRaw) {
      return "";
    }

    const rootState = JSON.parse(rootStateRaw);
    const authRaw = rootState?.auth;
    if (!authRaw) {
      return "";
    }

    const authState =
      typeof authRaw === "string" ? JSON.parse(authRaw) : authRaw;
    const loginData = authState?.IsLoginData || {};

    const candidates = [
      loginData?.currency,
      loginData?.currency_code,
      loginData?.currencyCode,
      loginData?.account?.currency,
    ];

    for (const value of candidates) {
      const normalized = normalizeCurrencyCode(value);
      if (normalized) {
        return normalized;
      }
    }
  } catch (error) {
    return "";
  }

  return "";
};

const getPreferredCurrency = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const localCurrency = normalizeCurrencyCode(localStorage.getItem("currency"));
  if (localCurrency) {
    return localCurrency;
  }

  return getCurrencyFromPersistedAuth();
};

const getConvertedPackagesUrl = () => {
  const params = new URLSearchParams();
  const token = getStoredSessionId();
  const currency = getPreferredCurrency();

  if (token) {
    params.append("token", token);
  }

  if (currency) {
    params.append("currency", currency);
  }

  const queryString = params.toString();
  return `${PaymentEndpoints.apiBaseUrl}packages${
    queryString ? `?${queryString}` : ""
  }`;
};

const getConvertCurrencyUrl = (from, to, amount) => {
  const params = new URLSearchParams();
  params.append("from", from);
  params.append("to", to);
  params.append("amount", String(amount));
  return `${PaymentEndpoints.apiBaseUrl}currency/convert?${params.toString()}`;
};

const parseCurrencyPrice = (value) => {
  if (value == null) {
    return { amount: NaN, currency: "" };
  }

  if (typeof value === "number") {
    return { amount: value, currency: "" };
  }

  const raw = String(value).trim();
  if (!raw) {
    return { amount: NaN, currency: "" };
  }

  const currencyMatch = raw.match(CURRENCY_CODE_REGEX);
  const numericPart = raw
    .replace(/[A-Za-z]/g, "")
    .replace(/,/g, "")
    .trim();
  const amount = Number(numericPart);

  return {
    amount,
    currency: currencyMatch ? currencyMatch[1].toUpperCase() : "",
  };
};

const normalizeCurrencyCode = (value) => {
  const normalized = (value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : "";
};

const convertPriceAtRuntime = async (from, to, amount, cache) => {
  if (!Number.isFinite(amount)) {
    return null;
  }
  if (!from || !to || from === to) {
    return amount;
  }

  const key = `${from}_${to}_${amount}`;
  if (cache.has(key)) {
    return cache.get(key);
  }

  const conversionResponse = await getAllData(
    getConvertCurrencyUrl(from, to, amount)
  );
  const convertedAmount = Number(
    conversionResponse?.data?.convertedAmount ?? conversionResponse?.convertedAmount
  );

  const result = Number.isFinite(convertedAmount) ? convertedAmount : null;
  cache.set(key, result);
  return result;
};

const applyRuntimeCurrencyConversion = async (packages, targetCurrency) => {
  const safeTargetCurrency = normalizeCurrencyCode(targetCurrency);
  if (!safeTargetCurrency || !Array.isArray(packages) || packages.length === 0) {
    return packages;
  }

  const cache = new Map();

  return Promise.all(
    packages.map(async (pkg) => {
      const parsed = parseCurrencyPrice(pkg?.purchase_price);
      const explicitSourceCurrency = normalizeCurrencyCode(parsed.currency);
      const fallbackSourceCurrency = normalizeCurrencyCode(pkg?.base_currency);
      const packageCurrency =
        explicitSourceCurrency ||
        fallbackSourceCurrency ||
        DEFAULT_PACKAGE_BASE_CURRENCY;
      const baseUsdAmount = Number(pkg?.base_purchase_price_usd);
      const sourceAmount = Number.isFinite(baseUsdAmount)
        ? baseUsdAmount
        : parsed.amount;

      if (!Number.isFinite(sourceAmount)) {
        return {
          ...pkg,
          display_currency: pkg?.display_currency || packageCurrency || safeTargetCurrency,
          currency: pkg?.currency || packageCurrency || safeTargetCurrency,
        };
      }

      const convertedAmount = await convertPriceAtRuntime(
        packageCurrency,
        safeTargetCurrency,
        sourceAmount,
        cache
      );

      const finalAmount = Number.isFinite(convertedAmount)
        ? convertedAmount
        : sourceAmount;

      return {
        ...pkg,
        purchase_price: toTwoDecimalString(finalAmount),
        display_currency: safeTargetCurrency,
        currency: safeTargetCurrency,
      };
    })
  );
};

export const getCasinoPackagesWithConvertedPrice = async () => {
  try {
    // Fetch packages directly without any currency conversion
    const casinoResponse = await getAllData(endPoints.api.GET_PACKAGE);
    const casinoPackages = Array.isArray(casinoResponse?.data)
      ? casinoResponse.data
      : [];

    return {
      status: casinoResponse?.status || "error",
      data: casinoPackages,
    };
  } catch (error) {
    console.error("Error fetching casino packages:", error);
    return {
      status: "error",
      data: [],
    };
  }
};
